import { Injectable, signal } from '@angular/core';
import { CongresoPorUrl } from '@core/models/congreso-por-url.model';

export type AppMode = 'admin' | 'congreso' | 'unknown';
export type AppModeResolutionState = 'pending' | 'resolved';

export interface AppCongresoData {
   sgiindice: number;
   congreso: string;
   nombre: string;
   nombrecorto: string;
   estado: string;
   config?: string;
}

@Injectable({
   providedIn: 'root'
})
export class AppModeService {
   private isResolutionCompleted = false;
   private resolveResolutionPromise!: () => void;
   private readonly resolutionPromise: Promise<void>;

   readonly mode = signal<AppMode>('unknown');
   readonly resolutionState = signal<AppModeResolutionState>('pending');
   readonly congresoData = signal<AppCongresoData | null>(null);

   constructor() {
      this.resolutionPromise = new Promise<void>(resolve => {
         this.resolveResolutionPromise = resolve;
      });
   }

   setAdmin(): void {
      this.mode.set('admin');
      this.congresoData.set(null);
      this.completeResolution();
   }

   setCongreso(data: CongresoPorUrl): void {
      this.mode.set('congreso');
      this.congresoData.set({
         sgiindice: Number(data.id),
         congreso: data.congreso ?? '',
         nombre: data.nombre ?? '',
         nombrecorto: data.nombrecorto ?? '',
         estado: data.estado ?? '',
         config: data.config ?? ''
      });
      this.completeResolution();
   }

   setUnknown(): void {
      this.mode.set('unknown');
      this.congresoData.set(null);
      this.completeResolution();
   }

   isAdmin(): boolean {
      return this.mode() === 'admin';
   }

   isCongreso(): boolean {
      return this.mode() === 'congreso';
   }

   isUnknown(): boolean {
      return this.mode() === 'unknown';
   }

   isResolved(): boolean {
      return this.resolutionState() === 'resolved';
   }

   waitUntilResolved(): Promise<void> {
      return this.isResolutionCompleted ? Promise.resolve() : this.resolutionPromise;
   }

   private completeResolution(): void {
      if (!this.isResolutionCompleted) {
         this.isResolutionCompleted = true;
         this.resolutionState.set('resolved');
         this.resolveResolutionPromise();
      }
   }
}
